import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DataSourcesPage } from "./DataSourcesPage";
import { WhatsappInstancesPage } from "./WhatsappInstancesPage";
import { UsersPage, type ManagedUser } from "./UsersPage";
import { PageHeader } from "../components/PageHeader";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { KpiTile } from "../components/intelligence/KpiTile";
import { AttentionPanel } from "../components/intelligence/AttentionPanel";
import { SystemHero, HeroNumber } from "../components/intelligence/SystemHero";
import { IconDatabase, IconWhatsApp, IconUser, IconCog, IconInfo } from "../components/icons";
import { settingsAttention, summarizeSettings, type SettingsDataSource, type SettingsInstance } from "../lib/settings";

type Tab = "datasources" | "whatsapp" | "users" | "general";

const TABS = [
  { id: "datasources" as Tab, label: "Bancos de Dados", icon: IconDatabase },
  { id: "whatsapp"    as Tab, label: "WhatsApp",         icon: IconWhatsApp },
  { id: "users"       as Tab, label: "Usuários",         icon: IconUser     },
  { id: "general"     as Tab, label: "Geral",            icon: IconCog      },
];

export function SettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("datasources");
  const admin = user?.role === "admin" && !user.isDemo;

  // Mesmas queryKeys das abas: a leitura do topo e a da aba usam o mesmo cache.
  const sources = useQuery<{ items: SettingsDataSource[] }>({ queryKey: ["data-sources"], queryFn: () => api.get("/data-sources"), enabled: admin, retryOnMount: false });
  const instances = useQuery<{ items: SettingsInstance[] }>({ queryKey: ["whatsapp-instances"], queryFn: () => api.get("/whatsapp/instances"), staleTime: 30_000, enabled: admin, retryOnMount: false });
  const users = useQuery<{ users: ManagedUser[] }>({ queryKey: ["users"], queryFn: () => api.get("/users"), staleTime: 30_000, enabled: admin, retryOnMount: false });

  const loading = sources.isLoading;
  const s = sources.data ? summarizeSettings(sources.data.items, instances.data?.items ?? []) : null;
  const activeUsers = users.data?.users.filter((u) => u.status === "active").length;
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

  return (
    <div className="space-y-6">
      <PageHeader title="Configurações" subtitle="Conexão com o ERP, WhatsApp, usuários e preferências do sistema" />

      {sources.error && <QueryErrorBanner what="as conexões do sistema" error={sources.error} onRetry={() => sources.refetch()} retrying={sources.isFetching} />}

      {!sources.error && <>
        <SystemHero loading={loading}
          status={s ? (!s.sourcesOk || s.sourcesError.length ? "critical" : s.instances && !s.instancesOk ? "warning" : "ok") : "ok"}
          statusText={s ? (s.sourcesOk && !s.sourcesError.length ? (s.instances && !s.instancesOk ? "WhatsApp desconectado" : "conexões em dia") : "ERP sem conexão") : undefined}
          headline={s ? (s.sourcesOk
            ? <>O BI está ligado a <HeroNumber>{s.sourcesOk}</HeroNumber> {s.sourcesOk === 1 ? "banco do ERP" : "bancos do ERP"}{s.companyCodes.length ? `, com ${plural(s.companyCodes.length, "empresa", "empresas")}` : ""}.</>
            : <>O BI não está ligado a nenhum banco do ERP.</>) : null}
          detail={s ? [
            instances.data ? (s.instances ? `WhatsApp: ${s.instancesOk} de ${plural(s.instances, "instância conectada", "instâncias conectadas")}.` : "WhatsApp não configurado.") : null,
            activeUsers != null ? `${plural(activeUsers, "usuário ativo", "usuários ativos")}.` : null,
          ].filter(Boolean).join(" ") : undefined} />

        <AttentionPanel points={s ? settingsAttention(s, instances.data?.items ?? null) : []} loading={loading}
          empty={{ title: "Nada pede ação", text: "A conexão com o ERP passou no último teste e o WhatsApp está conectado." }} />

        <section aria-labelledby="indicadores-titulo" className="space-y-3">
          <h2 id="indicadores-titulo" className="font-display font-semibold text-lg text-gray-900">Indicadores das conexões</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <KpiTile label="Conexões com o ERP" value={s ? `${s.sourcesOk} de ${s.sources}` : "—"} loading={loading}
              tone={s && (!s.sourcesOk || s.sourcesError.length) ? "critical" : null}
              footnote={s ? (s.sourcesError.length ? `${s.sourcesError.length} com erro no último teste` : s.sourcesUntested.length ? `${s.sourcesUntested.length} sem teste` : "Todas passaram no último teste") : undefined} />
            <KpiTile label="Empresas do ERP" value={s ? String(s.companyCodes.length) : "—"} loading={loading}
              footnote={s?.companyCodes.length ? `Códigos ${s.companyCodes.join(", ")}` : "Nenhum código configurado"} />
            <KpiTile label="WhatsApp" value={s && instances.data ? `${s.instancesOk} de ${s.instances}` : "—"} loading={loading || instances.isLoading}
              tone={s && s.instances && !s.instancesOk ? "warning" : null}
              footnote={instances.error ? "Sem acesso a esta informação" : "Instâncias conectadas"} />
            <KpiTile label="Usuários ativos" value={activeUsers != null ? String(activeUsers) : "—"} loading={loading || users.isLoading}
              footnote={users.error ? "Sem acesso a esta informação" : "Gestão completa em Governança"} />
          </div>
        </section>
      </>}

      <h2 className="font-display font-semibold text-lg text-gray-900">Ajustes</h2>
      <section className="card overflow-hidden">
        <div className="flex flex-wrap gap-1 border-b border-surface-border px-5" role="tablist" aria-label="Configurações">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} aria-controls={`config-${t.id}`} id={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-4 text-sm font-semibold ${tab === t.id ? "border-tm-500 text-tm-700" : "border-transparent text-gray-500 hover:text-gray-900"}`}>
              <t.icon className="h-4 w-4" />{t.label}
            </button>
          ))}
        </div>
        <div role="tabpanel" id={`config-${tab}`} aria-labelledby={`tab-${tab}`} className="p-5">
          {tab === "datasources" && <DataSourcesPage />}
          {tab === "whatsapp" && <div className="space-y-4">
            <SameAs text="É a mesma conexão da tela WhatsApp, aba Conexão." to="/whatsapp" label="Abrir WhatsApp" />
            <WhatsappInstancesPage />
          </div>}
          {tab === "users" && <div className="space-y-4">
            <SameAs text="É a mesma lista da tela Governança, que mostra também acessos e histórico." to="/governanca" label="Abrir Governança" />
            <UsersPage />
          </div>}
          {tab === "general" && <GeneralTab />}
        </div>
      </section>
    </div>
  );
}

function SameAs({ text, to, label }: { text: string; to: string; label: string }) {
  return <p className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-muted px-4 py-2.5 text-xs text-gray-600">
    <IconInfo className="h-4 w-4 flex-shrink-0 text-gray-400" />{text}
    <Link to={to} className="font-medium text-tm-700 underline">{label}</Link>
  </p>;
}

/**
 * Preferências ainda sem onde gravar (não há campo na API). Os controles mostram
 * o que o sistema usa hoje e ficam desligados, para não parecer que salvam.
 */
function GeneralTab() {
  const select = "w-full max-w-sm rounded-lg border border-surface-border bg-surface-muted px-3 py-2 text-sm text-gray-600";
  return (
    <div className="space-y-4">
      <p role="note" className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800">
        <IconInfo className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span>Estas preferências ainda não podem ser alteradas por aqui. Os valores abaixo são os que o sistema usa hoje em todas as telas.</span>
      </p>
      <div className="space-y-4">
        <label className="block text-xs font-medium text-gray-600">Fuso horário
          <select disabled defaultValue="sp" className={`mt-1 block ${select}`}>
            <option value="sp">Horário de Brasília (UTC-3)</option>
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-600">Período padrão
          <select disabled defaultValue="mes" className={`mt-1 block ${select}`}>
            <option value="mes">Mês atual</option>
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-600">Provedor de IA
          <select disabled defaultValue="srv" className={`mt-1 block ${select}`}>
            <option value="srv">Definido na configuração do servidor</option>
          </select>
        </label>
        <button className="btn-primary" disabled title="Ainda não disponível">Salvar preferências</button>
      </div>
    </div>
  );
}
